import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Clock, User, ArrowRight, ChefHat, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface BlogSectionProps {
  onRecipeClick?: () => void;
  onViewAllRecipesClick?: () => void;
}

const blogPosts = [
  {
    id: 1,
    title: 'The Art of Blending: Creating Perfect Spice Combinations',
    excerpt:
      'Learn the secrets behind creating harmonious spice blends that elevate any dish from ordinary to extraordinary.',
    image: 'https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=400&q=75&fm=webp',
    author: 'Chef Priya Sharma',
    readTime: '5 min read',
    date: 'Mar 15, 2024',
    category: 'Techniques',
    featured: true,
  },
  {
    id: 2,
    title: 'Authentic Chicken Tikka Masala Recipe',
    excerpt:
      'Master this restaurant-quality dish at home using our signature garam masala blend. Complete with step-by-step instructions.',
    image: 'https://images.unsplash.com/photo-1565557623262-b51c2513a641?w=400&q=75&fm=webp',
    author: 'Chef Priya Sharma',
    readTime: '8 min read',
    date: 'Mar 12, 2024',
    category: 'Recipes',
    rating: 5,
    isRecipe: true,
  },
  {
    id: 3,
    title: "Sourcing Stories: Our Journey to Kerala's Spice Gardens",
    excerpt:
      'Follow us on an exclusive journey to the spice gardens of Kerala, where we source the finest cardamom and black pepper.',
    image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&q=75&fm=webp',
    author: 'Sarah Mitchell',
    readTime: '7 min read',
    date: 'Mar 10, 2024',
    category: 'Stories',
  },
  {
    id: 4,
    title: 'Health Benefits of Turmeric: Beyond the Golden Color',
    excerpt:
      'Discover the science-backed health benefits of turmeric and how to maximize its potential in your daily cooking.',
    image: 'https://images.unsplash.com/photo-1615485290382-441e4d049cb5?w=400&q=75&fm=webp',
    author: 'Dr. James Chen',
    readTime: '6 min read',
    date: 'Mar 8, 2024',
    category: 'Health',
  },
];

export function BlogSection({
  onRecipeClick,
  onViewAllRecipesClick,
}: BlogSectionProps) {
  const { t } = useTranslation('blog');

  const handlePostClick = (post: any) => {
    if (post.isRecipe && onRecipeClick) {
      onRecipeClick();
    } else {
      // Handle regular blog post navigation
    }
  };

  return (
    <section id="blog" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {t('section.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('section.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-6">
            <Button
              variant="outline"
              className="border-amber-900 text-amber-900 hover:bg-amber-50"
              onClick={onViewAllRecipesClick}
            >
              <ChefHat className="h-4 w-4 mr-2" />
              {t('actions.viewAllRecipes')}
            </Button>
            <Button
              variant="outline"
              className="border-amber-900 text-amber-900 hover:bg-amber-50"
            >
              {t('actions.readBlog')}
            </Button>
          </div>
        </div>

        {/* Featured Post */}
        <div className="mb-12">
          <Card
            className="overflow-hidden md:hover:shadow-xl transition-shadow duration-300 cursor-pointer"
            onClick={() => handlePostClick(blogPosts[0])}
          >
            <div className="md:flex">
              <div className="md:w-1/2">
                <img
                  src={blogPosts[0].image}
                  alt={blogPosts[0].title}
                  className="w-full h-64 md:h-full object-cover"
                />
              </div>
              <div className="md:w-1/2 p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Badge className="bg-amber-500 text-white">
                    {t('badges.featured')}
                  </Badge>
                  <Badge variant="outline">{blogPosts[0].category}</Badge>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 md:hover:text-amber-900 transition-colors">
                  {blogPosts[0].title}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {blogPosts[0].excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      <span>{blogPosts[0].author}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span>{blogPosts[0].readTime}</span>
                    </div>
                    <span>{blogPosts[0].date}</span>
                  </div>
                  <Button
                    variant="ghost"
                    className="text-amber-900 hover:text-amber-800 hover:bg-amber-50"
                  >
                    {t('actions.readMore')}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Blog Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {blogPosts.slice(1).map(post => (
            <Card
              key={post.id}
              className="overflow-hidden md:hover:shadow-lg transition-all duration-300 cursor-pointer group"
              onClick={() => handlePostClick(post)}
            >
              <CardContent className="p-0">
                <div className="relative">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-48 object-cover md:group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4 flex gap-2">
                    <Badge
                      variant="secondary"
                      className="bg-white/90 text-gray-700"
                    >
                      {post.category}
                    </Badge>
                    {post.isRecipe && (
                      <Badge className="bg-amber-500 text-white">
                        <ChefHat className="h-3 w-3 mr-1" />
                        {t('badges.recipe')}
                      </Badge>
                    )}
                  </div>
                  {post.rating && (
                    <div className="absolute top-4 right-4 flex items-center gap-1 bg-white/90 px-2 py-1 rounded">
                      <Star className="h-3 w-3 text-yellow-400 fill-current" />
                      <span className="text-xs font-medium">{post.rating}</span>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-3 md:group-hover:text-amber-900 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        <span>{post.author}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                    <span>{post.date}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Newsletter Signup */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-8 text-center">
          <div className="max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {t('newsletter.title')}
            </h3>
            <p className="text-gray-600 mb-6">{t('newsletter.subtitle')}</p>
            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder={t('newsletter.placeholder')}
                className="flex-1 px-4 py-3 h-12 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              <Button className="bg-amber-900 hover:bg-amber-800 text-white px-6 h-12">
                {t('newsletter.subscribe')}
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              {t('newsletter.privacy')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
